// Market product specifications — what is worth showing a trader
// (owner request, 2026-10-19).
//
// Measured on the 205-product batch before writing a rule:
//   • placeholder values: 其他/其它 "Other" (110+), 无 "None" (45+), "/", 见详情
//     "see details" — "Brand: Other" alone on 37 products;
//   • attributes about the LISTING or the SELLER, not the product: whether the
//     seller is a general taxpayer (是否一般纳税人), whether the item is
//     imported, patent / copyright / IP flags, "domestic trade", "has a
//     shopping-guide video", installation service in China …;
//   • the same attribute twice under two Chinese names (加印LOGO ×2), and
//     yes/no attributes listed with BOTH values.
// So: drop placeholders, drop listing metadata, merge duplicates, and drop a
// yes/no attribute that says both. Everything else is kept as the supplier
// stated it — nothing is added or reworded here (translation happens at
// import; see supabase/functions/hiobuy-gateway/enrich.ts).
//
// Matching is on the source's own Chinese (value_zh / name_zh) first — the
// translations vary ("Other", "أخرى", "Other" left untranslated) while the
// Chinese does not — with English/Arabic placeholders as a second net.

import { specText } from './marketCatalog';

const PLACEHOLDER_ZH = new Set(['其他', '其它', '无', '/', '／', '-', '—', '见详情', '详见描述', '详见详情', '见描述', '暂无', '不详', '未知', '空', '无品牌', '其他品牌']);
const PLACEHOLDER_LATIN = new Set(['other', 'others', 'none', 'null', 'nil', 'n/a', 'na', 'nothing', 'see details', 'see description', 'unknown', '/', '-', '—', 'no brand']);
const PLACEHOLDER_AR = new Set(['أخرى', 'اخرى', 'آخر', 'لا يوجد', 'غير معروف', 'انظر التفاصيل', 'بدون علامة تجارية']);

// Attributes describing the listing or the seller — never the product.
const LISTING_ATTRIBUTES_ZH = new Set([
  '是否进口', '是否有专利', '专利', '是否专利货源', '版权', 'IP授权信息', '是否IP授权',
  '贸易属性', '是否外贸', '是否专供外贸', '是否跨境出口专供货源', '是否一般纳税人', '是否库存',
  '是否商场同款', '是否有导购视频', '是否提供安装服务', '主要下游平台', '主要销售地区', '货源类别',
  '有可授权的自有品牌', '是否支持一件代发', '是否包邮', '发货地', '产地', '上市年份季节', '上市时间',
  // Identifiers searchable back to the source factory or listing (brand rule:
  // the source is never exposed) — the seller's item code, manufacturer names,
  // certificate and licence numbers.
  '货号', '生产企业', '生产厂商', '3C证书编号', '证书编号', '无线电发射设备型号核准编码', '专利号', '全国工业生产许可证编号',
  '质检报告编号', '质检单位', '质检报告', '外贸出口认证',
  // Listing logistics and trade terms that are the seller's, not the product's.
  '最快出货时间', '最晚发货时间', '发货物流公司', '物流服务', '发票', '日生产量', '货源类型', '是否跨境货源', '图片实拍',
  '吊牌价', '价格段', '地市', '区县', '原产国/地区',
  '是否库存', '销售渠道类型', '外贸类型', '产品上市时间', '上市年份季节(上市时间)', '上市年份/季节',
  '出租车是否可运输', '是否配送上门', '可送货/安装', '是否需要自主安装', '是否有导购视频', 'AQL抽检标准', '吊牌', '领标', '是否可礼赠',
]);

// Canonical names for the specification names that recur across products.
// Translated once, by hand — the per-product machine translation produced
// several names for one attribute (and once "Slogan: Brandmianmian…").
// Anything not listed keeps its per-product translation.
const SPEC_NAMES = {
  '品牌': ['العلامة التجارية', 'Brand'], '型号': ['الموديل', 'Model'], '风格': ['الطراز', 'Style'], '材质': ['الخامة', 'Material'],
  '功能': ['المزايا', 'Features'], '产品类别': ['نوع المنتج', 'Product type'], '适用场景': ['الاستخدام', 'Use'], '图案': ['النقش', 'Pattern'],
  '规格': ['المواصفة', 'Specification'], '款式': ['التصميم', 'Design'], '适用人群': ['الفئة المستهدفة', 'Intended for'], '适用性别': ['الجنس', 'Gender'],
  '工艺': ['طريقة الصنع', 'Manufacturing'], '贸易属性': ['نوع التجارة', 'Trade type'], '容量': ['السعة', 'Capacity'], '形状': ['الشكل', 'Shape'],
  '重量': ['الوزن', 'Weight'], '毛重': ['الوزن الإجمالي', 'Gross weight'], '产品重量': ['وزن المنتج', 'Product weight'], '流行元素': ['عناصر التصميم', 'Design elements'],
  '安装方式': ['طريقة التركيب', 'Installation'], '适用季节': ['الموسم', 'Season'], '适合季节': ['الموسم', 'Season'], '厚薄': ['السماكة', 'Thickness'],
  '适用年龄': ['العمر المناسب', 'Age'], '适用年龄段': ['الفئة العمرية', 'Age group'], '适合年龄段': ['الفئة العمرية', 'Age group'], '包装': ['التغليف', 'Packaging'],
  '适用对象': ['مناسب لـ', 'Suitable for'], '外壳材质': ['خامة الهيكل', 'Housing material'], '保修期': ['الضمان', 'Warranty'], '开合方式': ['طريقة الفتح', 'Opening'],
  '售后服务': ['خدمة ما بعد البيع', 'After-sales service'], '功率': ['القدرة', 'Power'], '系列': ['السلسلة', 'Series'], '适用人数': ['عدد الأشخاص', 'Serves'],
  '电压': ['الجهد الكهربائي', 'Voltage'], '额定电压': ['الجهد الكهربائي', 'Rated voltage'], '额定功率': ['القدرة', 'Rated power'], '适用送礼场合': ['مناسبات الإهداء', 'Gift occasions'],
  '产品尺寸': ['مقاس المنتج', 'Product size'], '尺寸': ['المقاس', 'Size'], '材质成分': ['تركيبة الخامة', 'Material composition'], '颜色': ['اللون', 'Colour'],
  '是否智能': ['ذكي', 'Smart'], '适用范围': ['نطاق الاستخدام', 'Scope of use'], '认证': ['الشهادات', 'Certification'], '加热方式': ['طريقة التسخين', 'Heating'],
  '插头规格': ['نوع القابس', 'Plug type'], '插头标准': ['نوع القابس', 'Plug type'], '续航时间': ['مدة تشغيل البطارية', 'Battery life'], '厚度': ['السماكة', 'Thickness'],
  '收纳场景': ['مكان التخزين', 'Storage use'], '内部填充物': ['الحشوة', 'Filling'], '填充物': ['الحشوة', 'Filling'], '主面料成分': ['خامة القماش الرئيسية', 'Main fabric'],
  '适用场合': ['المناسبة', 'Occasion'], '面料名称': ['القماش', 'Fabric'], '鞋面材质': ['خامة الوجه', 'Upper material'], '鞋底材质': ['خامة النعل', 'Sole material'],
  '屏幕尺寸': ['حجم الشاشة', 'Screen size'], '屏幕类型': ['نوع الشاشة', 'Screen type'], '电池容量': ['سعة البطارية', 'Battery capacity'], '连接方式': ['طريقة الاتصال', 'Connection'],
  '蓝牙协议': ['إصدار البلوتوث', 'Bluetooth version'], '兼容平台': ['متوافق مع', 'Works with'], '防水等级': ['مقاومة الماء', 'Water resistance'], '防水性能': ['مقاومة الماء', 'Water resistance'],
  '充电方式': ['طريقة الشحن', 'Charging'], '接口类型': ['نوع المنفذ', 'Port'], '输出功率': ['قدرة الخرج', 'Output power'], '是否支持快充': ['شحن سريع', 'Fast charging'],
  '玩具类型': ['نوع اللعبة', 'Toy type'], '袖长': ['طول الكم', 'Sleeve length'], '领型': ['شكل الياقة', 'Collar'], '保温功能': ['حفظ الحرارة', 'Keeps warm'],
  '控制方式': ['طريقة التحكم', 'Controls'], '能效等级': ['فئة كفاءة الطاقة (الصين)', 'Energy class (China)'], '包装型式': ['شكل التغليف', 'Packaging form'], '用途': ['الاستخدام', 'Use'],
  '纸袋形状': ['شكل الكيس', 'Bag shape'], '是否为食品级材质': ['خامة صالحة للطعام', 'Food-grade material'], '腕带材质': ['خامة السوار', 'Strap material'], '表壳材质': ['خامة الهيكل', 'Case material'],
};
export const specName = (nameZh, lang) => { const n = SPEC_NAMES[String(nameZh ?? '').trim()]; return n ? (lang === 'ar' ? n[0] : n[1]) : null; };

const YES = new Set(['是', '有', '可以', '支持', 'yes', 'نعم']);
const NO = new Set(['否', '不可以', '不支持', 'no', 'لا']);

const norm = (s) => String(s ?? '').trim();
const isPlaceholder = (s) => {
  const zh = norm(s.value_zh);
  if (zh && PLACEHOLDER_ZH.has(zh)) return true;
  const en = norm(s.value_en).toLowerCase();
  const ar = norm(s.value_ar);
  if (!zh && !en && !ar) return true;
  if (!zh && en && PLACEHOLDER_LATIN.has(en)) return true;
  if (ar && (PLACEHOLDER_AR.has(ar) || PLACEHOLDER_LATIN.has(ar.toLowerCase())) && (!zh || PLACEHOLDER_ZH.has(zh))) return true;
  // Punctuation / symbols only.
  const shown = zh || en || ar;
  return !/[\p{L}\p{N}]/u.test(shown);
};

/**
 * The rows to show, in the page language: [{ name, value }].
 * Order is the supplier's; a merged duplicate takes the first position.
 */
export function cleanSpecs(specs, lang) {
  const out = [];
  const byName = new Map();
  for (const s of Array.isArray(specs) ? specs : []) {
    if (!s) continue;
    if (LISTING_ATTRIBUTES_ZH.has(norm(s.name_zh))) continue;
    if (isPlaceholder(s)) continue;
    const t = specText(s, lang);
    const value = t.value;
    const name = (lang === 'ar' || lang === 'en' ? specName(s.name_zh, lang) : null) || t.name;
    if (!norm(name) || !norm(value)) continue;
    const key = norm(name).toLowerCase();
    const prev = byName.get(key);
    const polarity = YES.has(norm(s.value_zh)) || YES.has(norm(s.value_en).toLowerCase()) ? 'yes'
      : NO.has(norm(s.value_zh)) || NO.has(norm(s.value_en).toLowerCase()) ? 'no' : null;
    if (!prev) {
      const row = { name: norm(name), values: [norm(value)], polarity: new Set(polarity ? [polarity] : []) };
      byName.set(key, row);
      out.push(row);
    } else {
      if (!prev.values.includes(norm(value))) prev.values.push(norm(value));
      if (polarity) prev.polarity.add(polarity);
    }
  }
  const sep = lang === 'ar' ? '، ' : lang === 'zh' ? '、' : ', ';
  // A yes/no attribute that says both is a contradiction, not information.
  return out.filter((r) => r.polarity.size < 2).map((r) => ({ name: r.name, value: r.values.join(sep) }));
}
